import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from 'reactstrap';
import { upsertSeasonExhibitors } from '../../ReduxToolkit/Reducers/fairManagement';
import { EXHIBITOR_FIELDS, formatExhibitorRows, parseExhibitorFile } from './exhibitorRows';

const MAX_ROWS = 5000;
const MAX_INVALID_SHOWN = 5;

// Header names a column is picked for automatically (letters only, lowercase)
const ALIASES = {
  name: ['customername', 'name', 'company', 'companyname'],
  website: ['website', 'url', 'domain', 'web'],
  type: ['type'],
  email: ['email', 'emailaddress', 'mail'],
  paid: ['registered', 'paid'],
  re_booked: ['rebookedcustomer', 'rebooked'],
};
const lettersOnly = (value) => String(value).toLowerCase().replace(/[^a-z]/g, '');
const autoMap = (headers) =>
  Object.fromEntries(
    EXHIBITOR_FIELDS.map((f) => [
      f.id,
      headers.find((h) => ALIASES[f.id].includes(lettersOnly(h))) || '',
    ])
  );

// Copy of the app's UploadExhibitorsSummary export: every invalid row as sent, with the reason
const exportInvalidCsv = (invalidRecords, rows) => {
  const csv = Papa.unparse(
    invalidRecords.map(({ index, reason }) => ({
      Row: index + 1,
      ...Object.fromEntries(EXHIBITOR_FIELDS.map((f) => [f.label, rows[index]?.[f.id] ?? ''])),
      Reason: reason,
    })),
    // Values come from the user's file and must not run as spreadsheet formulas
    { escapeFormulae: true }
  );
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  Object.assign(document.createElement('a'), {
    href: url,
    download: 'invalid-customers.csv',
  }).click();
  URL.revokeObjectURL(url);
};

// A CSV / Excel file of exhibitors into one season: pick the file, map its columns (the app's
// fields), upload, then the summary with the invalid rows
const UploadExhibitorsModal = ({ open, organizationId, season, onClose, onUploaded }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [fieldMap, setFieldMap] = useState({});
  const [saving, setSaving] = useState(false);
  // { message, summary, invalid_records, rows }
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setFieldMap({});
      setResult(null);
    }
  }, [open]);

  const headers = file?.columns?.map((c) => c.accessor) || [];

  const handleFile = async (picked) => {
    if (!picked) return;
    try {
      const parsed = await parseExhibitorFile(picked);
      const columns = parsed.columns.map((c) => c.accessor);
      setFile({ name: picked.name, ...parsed });
      setFieldMap(autoMap(columns));
    } catch (error) {
      toast.error(error.message, { autoClose: 3000 });
    }
  };

  const handleUpload = async () => {
    const missing = EXHIBITOR_FIELDS.filter((f) => f.required && !fieldMap[f.id]);
    if (missing.length) {
      return toast.warning(`Map ${missing.map((f) => f.label).join(', ')}`, { autoClose: 3000 });
    }
    // formatExhibitorRows takes { [csvHeader]: fieldId }
    const mapping = Object.fromEntries(
      Object.entries(fieldMap)
        .filter(([, header]) => header)
        .map(([field, header]) => [header, field])
    );
    const rows = formatExhibitorRows(file.data, mapping);
    if (!rows.length) return toast.warning('No customers found in the mapped columns', { autoClose: 3000 });
    if (rows.length > MAX_ROWS) {
      return toast.warning(`You can upload up to ${MAX_ROWS} customers at once`, { autoClose: 3000 });
    }

    setSaving(true);
    const response = await dispatch(upsertSeasonExhibitors(organizationId, season.id, rows));
    setSaving(false);
    if (response) {
      setResult({ message: response.message, ...response.data, rows });
      if (response.data.summary.added) onUploaded?.();
    }
  };

  const close = saving ? undefined : onClose;

  return (
    <Modal isOpen={open} toggle={close} centered size="lg">
      <ModalHeader toggle={close}>
        Upload exhibitors to {season?.season_name} {season?.year}
      </ModalHeader>
      <ModalBody>
        {result ? (
          <>
            <p>{result.message}</p>
            <ul className="mb-3">
              <li>Added: {result.summary.added}</li>
              <li>
                Duplicates skipped: {result.summary.duplicates_in_payload + result.summary.already_exists}
              </li>
              <li>Invalid: {result.summary.invalid}</li>
            </ul>
            {!!result.invalid_records.length && (
              <>
                <h6>Invalid rows</h6>
                <ul>
                  {result.invalid_records.slice(0, MAX_INVALID_SHOWN).map(({ index, reason }) => (
                    <li key={index}>
                      Row {index + 1}
                      {result.rows[index]?.name ? ` (${result.rows[index].name})` : ''}: {reason}
                    </li>
                  ))}
                </ul>
                {result.invalid_records.length > MAX_INVALID_SHOWN && (
                  <p className="text-muted">
                    +{result.invalid_records.length - MAX_INVALID_SHOWN} more
                  </p>
                )}
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={() => exportInvalidCsv(result.invalid_records, result.rows)}
                >
                  Export invalid rows (CSV)
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            <FormGroup>
              <Label>File (.csv, .xls or .xlsx)</Label>
              <Input
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={(e) => handleFile(e.target.files?.[0])}
                disabled={saving}
              />
            </FormGroup>
            {file && (
              <>
                <p className="text-muted">
                  {file.name}: {file.data.length} rows. Map the file's columns:
                </p>
                <Table size="sm" borderless>
                  <tbody>
                    {EXHIBITOR_FIELDS.map((field) => (
                      <tr key={field.id}>
                        <td style={{ width: '40%' }}>
                          {field.label}
                          {field.required && <span className="text-danger"> *</span>}
                        </td>
                        <td>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={fieldMap[field.id] || ''}
                            onChange={(e) =>
                              setFieldMap((current) => ({ ...current, [field.id]: e.target.value }))
                            }
                          >
                            <option value="">Not in the file</option>
                            {headers.map((header) => (
                              <option key={header} value={header}>
                                {header}
                              </option>
                            ))}
                          </Input>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {result ? (
          <Button color="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button color="primary" outline onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleUpload} disabled={saving || !file}>
              {saving ? <Spinner size="sm" color="light" /> : 'Upload'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UploadExhibitorsModal;
